"use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Mail, Phone, Search, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import { ONBOARDING_PHONE_COUNTRIES } from "@/const/worldCountryCodes";

export interface CreateAccountDetails {
  fullName: string;
  phoneNumber: string;
}

interface CreateAccountStepProps {
  email: string;
  onboardingReason?: string | null;
  isFromSwitchGuest?: boolean;
  isLoading?: boolean;
  error?: string;
  onChange?: (details: CreateAccountDetails) => void;
  onSubmit: (details: CreateAccountDetails) => void | Promise<void>;
  onBack: () => void;
}

const DEFAULT_COUNTRY = ONBOARDING_PHONE_COUNTRIES.find((c) => c.code === "CO")!;

const CreateAccountStep: React.FC<CreateAccountStepProps> = ({
  email,
  onboardingReason,
  isFromSwitchGuest,
  isLoading,
  error,
  onChange,
  onSubmit,
  onBack,
}) => {
  const { language } = useAuth();
  const [fullName, setFullName] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [phoneInput, setPhoneInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const phoneNumber = phoneInput.trim() ? `${selectedCountry.dialCode} ${phoneInput.trim()}` : "";

  const t = (key: string, fallback: string) =>
    dictionary[key]?.[language as Language] ?? fallback;

  const filteredCountries = ONBOARDING_PHONE_COUNTRIES.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dialCode.includes(search)
  );

  useEffect(() => {
    onChange?.({ fullName, phoneNumber });
  }, [fullName, phoneNumber, onChange]);

  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch("");
    }
  }, [dropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const bannerText =
    isFromSwitchGuest
      ? t("switch-guest-welcome-banner", "Bienvenido a Fluxi! Ingresa tus datos a continuación para crear tu cuenta.")
      : onboardingReason === "missing_user_detail"
        ? t("missing-user-detail-banner", "Aún nos faltan unos datos en tu cuenta.")
        : t(
          "create-account-banner",
          "No encontramos una cuenta con el correo [email]. Completa tus datos para crear tu cuenta."
        ).replace("[email]", email.trim());

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ fullName, phoneNumber });
      }}
      className="space-y-5 md:space-y-6 w-full"
    >
      <div className="bg-white/90 rounded-2xl px-4 py-4 md:px-5 md:py-5 text-center border border-white/60 shadow-sm">
        <p className="m-0 text-sm md:text-base text-[#2D3240]">{bannerText}</p>
      </div>

      <h2 className="text-[38px] md:text-[46px] font-bold text-white text-center leading-tight">
        {t("create-account-title", "Crear cuenta")}
      </h2>

      <div className="space-y-4">
        {/* Full name */}
        <div className="space-y-2">
          <label className="block text-white text-lg md:text-xl font-semibold">
            {t("name", "Nombre")}
          </label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full bg-white px-12 py-3 md:py-4 text-base border border-white rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t("full-name-placeholder", "Nombre Completo")}
              disabled={isLoading}
              autoFocus
            />
          </div>
        </div>

        {/* Email (read-only) */}
        <div className="space-y-2">
          <label className="block text-white text-lg md:text-xl font-semibold">
            {t("email-label", "Correo electrónico")}
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="email"
              value={email}
              readOnly
              className="w-full bg-gray-200/80 px-12 pr-12 py-3 md:py-4 text-base border border-white rounded-xl text-gray-700"
            />
            <CheckCircle2 className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-green-500" />
          </div>
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <label className="block text-white text-lg md:text-xl font-semibold">
            {t("phone-label", "Celular")}
          </label>
          <div className="flex gap-2">
            {/* Country code dropdown */}
            <div ref={dropdownRef} className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setDropdownOpen((v) => !v)}
                disabled={isLoading}
                className="h-full bg-white px-3 py-3 md:py-4 border border-white rounded-xl flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 min-w-[64px]"
              >
                <span className={`fi fi-${selectedCountry.code.toLowerCase()} rounded-sm`} style={{ width: "1.4em", height: "1.05em", display: "inline-block" }} />
              </button>

              {dropdownOpen && (
                <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                  {/* Search */}
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        ref={searchRef}
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("search-country-placeholder", "Buscar país o código...")}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Country list */}
                  <ul className="max-h-52 overflow-y-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((c) => (
                        <li key={c.code}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCountry(c);
                              setDropdownOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-blue-50 transition-colors text-left ${
                              c.code === selectedCountry.code ? "bg-blue-50" : ""
                            }`}
                          >
                            <span className={`fi fi-${c.code.toLowerCase()} rounded-sm flex-shrink-0`} style={{ width: "1.4em", height: "1.05em", display: "inline-block" }} />
                            <span className="flex-1 truncate text-gray-800">{c.name}</span>
                            <span className="text-gray-400 flex-shrink-0 text-xs">{c.dialCode}</span>
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="px-4 py-4 text-sm text-gray-400 text-center">
                        {t("no-countries-found", "No se encontraron países")}
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Phone number input */}
            <div className="relative flex-1">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="tel"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                className="w-full bg-white px-12 py-3 md:py-4 text-base border border-white rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t("phone-number-placeholder", "300 123 4567")}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {error ? (
          <p className="mt-1 text-xs md:text-sm text-red-200 animate-fade-in text-center">
            {error}
          </p>
        ) : null}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-[#85A2FF] text-white py-4 px-6 rounded-full text-base md:text-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            <span>{t("sending-code", "Enviando código...")}</span>
          </>
        ) : (
          <>
            <span>{t("send-code", "Enviar código")}</span>
            <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform duration-200" />
          </>
        )}
      </button>
      <button
        onClick={onBack}
        className="w-full bg-[#85A2FF] text-white py-2.5 md:py-3 px-4 rounded-lg text-sm md:text-base font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowLeft className="w-3 h-3 md:w-4 md:h-4 mr-2 flex-shrink-0" />
        <span className="text-xs md:text-sm truncate">{t("change-email", "Cambiar correo electrónico")}</span>
      </button>
    </form>
  );
};

export default CreateAccountStep;
